import { useEffect, useState } from "react"

import acc from "accounting"
import { App, Col, Divider, Flex, Row, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useSearchParams } from "react-router-dom"

import InputComponent from "../../../Components/Input"
import withPermission from "../../../hoc/withPermission"
import Private, { INCOGNITO_MASK } from "../../../Components/Private"
import { useExternalDemoMode } from "../../../hooks/useExternalDemoMode"
import Images from "../../../Images"
import { CARD_BRANDS } from "../../../Utils/cardBrands"
import { selectIncognito } from "../../../Utils/incognito"
import { normalizeMoney } from "../../../Utils/normalizeMoney"
import { highAccessRoles } from "../../../Utils/permission"
import utils from "../../../Utils/utils"
import { AddFundsModal } from "../../Balance/components/AddFundsModal"
import { useShopifyOnlyUser } from "../../Balance/hooks/useShopifyOnlyUser"
import { balanceActions } from "../../Balance/redux/balance.slice"
import { updateBalance } from "../../Balance/redux/balance.thunks"

import "./AutomaticTopUpBalance.less"

const { Text } = Typography

const PermissionInputHigh = withPermission(InputComponent, highAccessRoles)

const AutomaticTopUpBalance = ({
  theme,
  balanceData,
  ba_role,
  defaultCard,
  topUpRef,
  isAdmin,
  userInfo,
  isMobile,
  incognito,
  ...props
}) => {
  const { t } = useTranslation()
  const { notification } = App.useApp()
  const [searchParams] = useSearchParams()
  const userId = searchParams.get("user_id")
  const externalDemoMode = useExternalDemoMode()
  const { isShopifyOnlyUser } = useShopifyOnlyUser()

  const { fetching, balance, saveFetching, saveResult } = balanceData
  const businessAccountRole = ba_role
  const hasBalanceSettingsAccess = highAccessRoles.includes(Number(businessAccountRole))
  const isDemoModeBlocked =
    !userInfo?.new_onboarding_finished && userInfo?.next_step < 8 && !isAdmin
  const [amounts, setAmounts] = useState({
    min_amount: normalizeMoney(balance?.["min_amount"] || 0),
    recommended: normalizeMoney(balance?.["recommended"] || 0)
  })

  const has_bussiness_account = balance?.enable_to_topup;
  const areInputsDisabled =
    !hasBalanceSettingsAccess &&
    (!has_bussiness_account || externalDemoMode || isDemoModeBlocked)

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  useEffect(() => {
    if (saveResult === "balance_update") {
      notification.success({
        message: t("Automatic top-up settings updated"),
        description: t("The amount is applied for your account."),
        ...notificationConfig
      })
      props.balanceSaveResultReset()
    }
  }, [saveResult])

  useEffect(() => {
    setAmounts({
      min_amount: normalizeMoney(balance?.["min_amount"] || 0),
      recommended: normalizeMoney(balance?.["recommended"] || 0)
    })
  }, [balance?.["min_amount"], balance?.["recommended"]])

  const onChangeHandler = (e) => {
    const { name, value } = e.target
    setAmounts((prev) => ({ ...prev, [name]: value }))
  }

  const onBlurHandler = (e) => {
    const { name, value } = e.target
    const normalizedValue = normalizeMoney(value)
    const calculatedValue = balance?.[`calculated_${name}`]
    const nextValue =
      !normalizedValue || +normalizedValue < +calculatedValue
        ? normalizeMoney(calculatedValue)
        : normalizedValue

    setAmounts((prev) => ({ ...prev, [name]: nextValue }))

    if (+nextValue !== +balance?.[`${name}`]) {
      props.updateBalance({
        [name]: +nextValue,
        ...(isAdmin && { user_id: userId })
      })
    }
  }

  return (
    <>
      <Row>
        <Col flex={"1 1 300px"} style={{ marginBottom: isMobile ? 16 : 0 }}>
          <Space size={8} direction={"vertical"}>
            <Text strong className="auto-top-up-title">
              {isShopifyOnlyUser ? "How you will pay" : t("How and when you will pay")}
            </Text>
            <Text type="secondary">
              {t(
                "Set up automatic top-ups for your balance to ensure it never falls below a specified amount."
              )}
            </Text>
          </Space>
        </Col>
        <Col flex={"0 1 431px"}>
          <Flex vertical gap={16}>
            {defaultCard && !isShopifyOnlyUser && (
              <>
                <Flex vertical gap={4}>
                  <Text type="secondary">{t("Payment method")}</Text>
                  <Flex gap={8} align={"center"}>
                    {CARD_BRANDS.includes(defaultCard?.brand) && (
                      <img
                        src={Images[`${defaultCard?.brand}${theme === "dark" ? "_dark" : ""}`]}
                        width={20}
                        height={20}
                        alt={defaultCard.brand}
                      />
                    )}
                    <Text strong style={{ fontSize: 16 }}>
                      {utils.toUpperLatter(defaultCard?.["brand"])}
                    </Text>
                    <Divider type="vertical" style={{ margin: 0, height: 16 }} />
                    {defaultCard?.["last4"] && (
                      <Text strong style={{ fontSize: 16 }}>
                        <Private mask="••••">{defaultCard?.["last4"]}</Private>
                      </Text>
                    )}
                  </Flex>
                </Flex>

                <Divider style={{ margin: 0 }} />
              </>
            )}
            <div ref={topUpRef} className="auto-top-up-inputs-container">
              <Flex vertical gap={8}>
                <Text strong>{t("When balance goes below")}</Text>
                <PermissionInputHigh
                  key={balance?.["min_amount"] || 1}
                  name="min_amount"
                  placeholder={0.0}
                  value={amounts.min_amount}
                  onChange={onChangeHandler}
                  inputMode="decimal"
                  style={{ width: "100%" }}
                  onBlur={onBlurHandler}
                  disabled={areInputsDisabled}
                  role={businessAccountRole}
                  type={incognito ? "password" : "text"}
                />
                <Text type="secondary">
                  {t("Please enter an amount greater than $_value_", {
                    value: incognito
                      ? INCOGNITO_MASK
                      : acc.formatNumber(balance?.["calculated_min_amount"] || 150, 2, ".")
                  })}
                </Text>
              </Flex>

              <Flex vertical gap={8}>
                <Text strong>{t("Fill my balance up to")}</Text>
                <PermissionInputHigh
                  key={balance?.["recommended"] || 1}
                  name="recommended"
                  placeholder={0.0}
                  value={amounts.recommended}
                  onChange={onChangeHandler}
                  inputMode="decimal"
                  style={{ width: "100%" }}
                  onBlur={onBlurHandler}
                  disabled={areInputsDisabled}
                  role={businessAccountRole}
                  type={incognito ? "password" : "text"}
                />
                <Text type="secondary">
                  {t("Please enter an amount greater than $_value_", {
                    value: incognito
                      ? INCOGNITO_MASK
                      : acc.formatNumber(balance?.["calculated_recommended"] || 225, 2, ".")
                  })}
                </Text>
              </Flex>
            </div>
          </Flex>
        </Col>
      </Row>

      {!externalDemoMode && <AddFundsModal />}
    </>
  )
}

const mapStateToProps = (state) => ({
  //isMobile: state.nav.isMobile,
  theme: state.nav.theme,
  isAdmin: state.auth.isAdmin,
  ba_role: state.auth.userInfo?.ba_role?.id,
  defaultCard: state?.user?.usersCards?.find((card) => card.is_default),
  //businessAccount: state.user.businessAccount,
  balanceData: state.balance,
  userInfo: state.auth.userInfo,
  incognito: selectIncognito(state)
})

const mapDispatchToProps = (dispatch) => ({
  updateBalance: (data) => dispatch(updateBalance(data)),
  balanceSaveResultReset: () =>
    dispatch(balanceActions.balanceSetProp({ key: "saveResult", value: null }))
})

export default connect(mapStateToProps, mapDispatchToProps)(AutomaticTopUpBalance)
